import { z } from 'zod';
/** A person's explicit stage selection. Offsets count Unicode characters,
 * from inclusive to exclusive; absent range means the complete source. */
export const chatSourceSelectionSchema = z.array(z.object({
    materialId: z.string().uuid(),
    range: z.object({ from: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER), to: z.number().int().positive().max(Number.MAX_SAFE_INTEGER) }).strict().refine(r => r.to > r.from).optional(),
}).strict()).refine(rows => new Set(rows.map(row => row.materialId)).size === rows.length, { message: 'Select each source once.' });
export const chatHistorySelectionSchema = z.object({
    fromSeq: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    toSeq: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
}).strict().refine(range => range.toSeq >= range.fromSeq, { message: 'Choose an ordered conversation range.' });
export const todayWorkspaceSchema = z.object({
    span: z.enum(['day', 'week', 'last2', 'next2', 'month']).default('day'),
    anchor: z.string().datetime({ offset: true }).optional(),
    date: z.string().date().optional(),
    offset: z.number().int().min(0).default(0),
    limit: z.number().int().min(1).max(100).default(50),
    departmentFilter: z.string().regex(/^[a-z][a-z_]*$/).nullable().optional(),
    kind: z.enum(['calendar_event', 'task', 'reply_needed', 'brief', 'chase', 'shared_inbox', 'merge_proposal', 'break', 'note']).optional(),
}).strict().refine(value => !(value.date && value.anchor), { message: 'Choose a local date or an instant, not both.' });
export const todayRecordCallSchema = z.object({
    scopeDepartment: z.string().regex(/^[a-z][a-z_]*$/),
    path: z.string().min(1).max(160),
    payload: z.record(z.string(), z.unknown()),
}).strict();
/** Frozen business surface. New endpoints require deliberate review here. */
export const TODAY_BUSINESS_PATHS = new Set([
    // Existing business handlers reached by the reusable views. Each retains
    // its own source permissions, human decisions and send controls.
    '/v1/attachments/drive',
    '/v1/attachments/list',
    '/v1/attachments/upload',
    '/v1/audit/twin',
    '/v1/builds/approve',
    '/v1/builds/return',
    '/v1/communications/capabilities',
    '/v1/communications/manual/attachments/download',
    '/v1/communications/manual/attachments/upload',
    '/v1/communications/manual/create',
    '/v1/communications/manual/list',
    '/v1/communications/tasks/create',
    '/v1/cost/summary',
    '/v1/distributors/research',
    '/v1/duplicates/canonical',
    '/v1/duplicates/consolidate',
    '/v1/duplicates/decide',
    '/v1/duplicates/evaluations/backfill',
    '/v1/duplicates/evaluations/queue',
    '/v1/duplicates/history',
    '/v1/duplicates/list',
    '/v1/duplicates/policies',
    '/v1/duplicates/policies/enable',
    '/v1/duplicates/policies/stop',
    '/v1/duplicates/reverse',
    '/v1/employments/move',
    '/v1/erasure/request',
    '/v1/evidence/decide',
    '/v1/evidence/proposals',
    '/v1/experience/personalise',
    '/v1/incidents/acknowledge',
    '/v1/incidents/get',
    '/v1/incidents/list',
    '/v1/insight/pipeline',
    '/v1/intros/answer',
    '/v1/intros/list',
    '/v1/lifecycle/contact',
    '/v1/lifecycle/contacts',
    '/v1/lifecycle/import',
    '/v1/lifecycle/record',
    '/v1/lifecycle/sync',
    '/v1/lifecycle/track',
    '/v1/lifecycle/withdraw',
    '/v1/loop/abandon',
    '/v1/loop/confirm',
    '/v1/loop/list',
    '/v1/loop/preview',
    '/v1/loop/start',
    '/v1/loop/status',
    '/v1/mailboxes/list',
    '/v1/mailing',
    '/v1/mailing/end',
    '/v1/mailing/requests',
    '/v1/mailings',
    '/v1/mailings/create',
    '/v1/plans/dry-run',
    '/v1/plans/list',
    '/v1/plans/rests',
    '/v1/profile-changes/decide',
    '/v1/profile-changes/list',
    '/v1/prospects/details',
    '/v1/prospects/erase',
    '/v1/prospects/intro',
    '/v1/prospects/lost',
    '/v1/prospects/objection',
    '/v1/prospects/opt-out',
    '/v1/prospects/sample',
    '/v1/publications/decide',
    '/v1/removals/decline',
    '/v1/samples/list',
    '/v1/samples/outcome',
    '/v1/strategies/decide',
    '/v1/strategies/list',
    '/v1/subjects/confirm',
    '/v1/subjects/decide',
    '/v1/subjects/evaluations/backfill',
    '/v1/subjects/evaluations/queue',
    '/v1/subjects/policies',
    '/v1/subjects/policies/enable',
    '/v1/subjects/policies/stop',
    '/v1/subjects/reviews',
    '/v1/tools/list',
    '/v1/counterparties/approve',
    '/v1/unresolved/know-and-approve',
    '/v1/prospects/approve-company',
    '/v1/outreach/campaigns/approve',
    '/v1/mailings/approve',
    '/v1/cover/list',
    '/v1/tools/attachments',
    '/v1/unresolved/list',
    '/v1/unresolved/promote',
    '/v1/unresolved/dismiss',
    '/v1/unresolved/digest-time',
    '/v1/unresolved/research/policies/enable',
    '/v1/unresolved/research/policies/stop',
    '/v1/unresolved/research/queue',
    '/v1/unresolved/research/priority',
    "/v1/calendar/events",
    "/v1/calendar/slots",
    "/v1/availability/get",
    "/v1/availability/set",
    "/v1/briefs/live",
    "/v1/briefs/compose",
    "/v1/threads/list",
    "/v1/tasks/list",
    "/v1/tasks/reviews",
    "/v1/tasks/reviews/postpone",
    "/v1/tasks/get",
    "/v1/tasks/close",
    "/v1/tasks/board",
    "/v1/tasks/update",
    "/v1/tasks/create",
    "/v1/tasks/assign",
    "/v1/tasks/assignees",
    "/v1/tasks/remove",
    "/v1/threads/remove",
    "/v1/directions/list",
    "/v1/directions/add",
    "/v1/directions/revoke",
    "/v1/suppliers/compare",
    "/v1/suppliers/samples",
    "/v1/suppliers/samples/receive",
    "/v1/suppliers/samples/judge",
    "/v1/buyers/answers",
    "/v1/buyers/answers/add",
    "/v1/registers/list",
    "/v1/registers/write",
    "/v1/institutions/filing/configure",
    "/v1/institutions/filing/run",
    "/v1/institutions/documents/check",
    "/v1/institutions/drafts/reconcile",
    "/v1/institutions/list",
    "/v1/institutions/read",
    "/v1/institutions/drafts/reference",
    "/v1/institutions/create",
    "/v1/institutions/configure",
    "/v1/institutions/own-mail/options",
    "/v1/institutions/own-mail/link",
    "/v1/institutions/proposals/capture",
    "/v1/institutions/proposals/decide",
    "/v1/institutions/assessments/record",
    "/v1/institutions/portal/review",
    "/v1/institutions/contacts/record",
    "/v1/institutions/requirements/revise",
    "/v1/institutions/packages/draft",
    "/v1/institutions/packages/review",
    "/v1/institutions/submissions/record",
    "/v1/institutions/interactions/record",
    "/v1/institutions/decisions/record",
    "/v1/outreach/sequences/reviews",
    "/v1/outreach/sequences/review/ask",
    "/v1/outreach/sequences/review/answer",
    "/v1/signals/supply-risks",
    "/v1/signals/supply-risk/add",
    "/v1/suppliers/research",
    "/v1/suppliers/candidates",
    "/v1/suppliers/candidates/add",
    "/v1/suppliers/contacts/add",
    "/v1/suppliers/decide",
    "/v1/suppliers/decisions",
    "/v1/suppliers/prepare-email",
    "/v1/suppliers/contacted",
    "/v1/tasks/notes/list",
    "/v1/tasks/notes/add",
    "/v1/tasks/elaborate",
    "/v1/tasks/approve-held",
    "/v1/records/elaborate",
    "/v1/records/elaborations",
    "/v1/requests/uses/find",
    "/v1/requests/uses",
    "/v1/requests/uses/decide",
    "/v1/tasks/add",
    "/v1/shortlists/propose",
    "/v1/shortlists/list",
    "/v1/shortlists/get",
    "/v1/shortlists/decide",
    "/v1/prospecting/brief",
    "/v1/tasks/schedule",
    "/v1/drafts/redraft",
    "/v1/threads/open",
    "/v1/threads/read",
    "/v1/threads/resolve",
    "/v1/threads/claim",
    "/v1/threads/unclaim",
    "/v1/drafts/compose",
    "/v1/drafts/pending",
    "/v1/drafts/decide",
    "/v1/drafts/authority",
    "/v1/tasks/twin/list",
    "/v1/tasks/twin/read",
    "/v1/threads/get",
    "/v1/drafts/list",
    "/v1/companies/workspace",
    "/v1/companies/operations",
    "/v1/companies/roles/record",
    "/v1/companies/groups/link",
    "/v1/companies/enrichment/propose",
    "/v1/companies/enrichment/decide",
    "/v1/companies/sources/refresh",
    "/v1/companies/backfill",
    "/v1/contacts/update",
    "/v1/contacts/assign",
    "/v1/companies/update",
    "/v1/records/incoming",
    "/v1/flex/entities",
    "/v1/flex/list",
    "/v1/flex/get",
    "/v1/flex/write",
    "/v1/requests/list",
    "/v1/requests/read",
    '/v1/requests/standing',
    '/v1/tasks/standing',
    "/v1/requests/open",
    "/v1/requests/acknowledge",
    "/v1/requests/complete",
    "/v1/requests/refuse",
    "/v1/requests/cancel",
    "/v1/reports/activity",
    "/v1/outreach/markets",
    "/v1/markets/list",
    "/v1/markets/screen",
    "/v1/markets/get",
    "/v1/markets/decide",
    "/v1/markets/confirm-route",
    "/v1/markets/evidence",
    "/v1/markets/research",
    "/v1/markets/sources",
    "/v1/markets/report",
    "/v1/documents/create",
    "/v1/documents/list",
    "/v1/companies/details",
    "/v1/reports/prospecting",
    "/v1/buyers/list",
    "/v1/buyers/create",
    "/v1/buyers/approve",
    "/v1/suppliers/list",
    "/v1/suppliers/get",
    "/v1/suppliers/create",
    "/v1/suppliers/answer",
    "/v1/suppliers/confirm",
    "/v1/suppliers/ask",
    "/v1/suppliers/asked",
    "/v1/suppliers/contact",
    "/v1/suppliers/find-contact",
    "/v1/offers/list",
    "/v1/offers/get",
    "/v1/offers/create",
    "/v1/offers/prepare",
    "/v1/offers/sent",
    "/v1/offers/send-to",
    "/v1/offers/recipient-sent",
    "/v1/profiles/list",
    "/v1/profiles/create",
    "/v1/profiles/suggest",
    "/v1/profiles/live",
    "/v1/territories/list",
    "/v1/territories/get",
    "/v1/territories/create",
    "/v1/prospecting/setup",
    "/v1/signals/list",
    "/v1/prospecting/batch",
    "/v1/prospecting/index",
    "/v1/outreach/suppression",
    "/v1/outreach/suppression/check",
    "/v1/outreach/discover",
    "/v1/outreach/prospects",
    "/v1/outreach/prospect",
    "/v1/outreach/prospect/exclude",
    "/v1/outreach/prospect/draft",
    "/v1/outreach/prospect/retry",
    "/v1/outreach/prospects/import",
    "/v1/outreach/campaigns",
    "/v1/outreach/campaign",
    "/v1/outreach/campaigns/create",
    "/v1/outreach/campaigns/approve",
    "/v1/outreach/campaign/end",
    "/v1/outreach/queue",
    "/v1/outreach/complaint",
    "/v1/outreach/report",
    "/v1/approvals/queue",
    "/v1/requests/standing",
    "/v1/tasks/standing",
    "/v1/briefs/list",
    "/v1/briefs/read",
    "/v1/merges/list",
    "/v1/merges/decide",
    "/v1/threads/merge",
    "/v1/projects/list",
    "/v1/projects/get",
    "/v1/projects/create",
    "/v1/projects/add-member",
    "/v1/projects/draft",
    "/v1/projects/room",
    "/v1/projects/room/say",
    "/v1/projects/meetings/convene",
    "/v1/calendar/hold",
    "/v1/calendar/release",
    "/v1/calendar/invite",
    "/v1/calendar/move",
    "/v1/calendar/cancel",
    "/v1/calendar/following",
    "/v1/notifications/list",
    "/v1/notifications/read",
    "/v1/calendar/capture",
    "/v1/commitments/list",
    "/v1/commitments/confirm",
    "/v1/commitments/dismiss",
    "/v1/outreach/suppression/lift-request",
    "/v1/outreach/suppression/lift-requests",
    "/v1/outreach/sequences",
    "/v1/outreach/sequences/create",
    "/v1/outreach/sequences/approve",
    "/v1/outreach/sequences/retire",
]);
// Read-only identity/reference lookups plus the person's existing chat paths.
for (const path of ["/v1/chat/open", "/v1/chat/say", "/v1/chat/read", "/v1/chat/close", "/v1/chat/context", "/v1/chat/agents", "/v1/chat/task", "/v1/chat/material/list", "/v1/chat/material/upload", "/v1/chat/material/drive", "/v1/chat/twin-plan", "/v1/chat/twin-plan/latest", "/v1/chat/twin-plan/read", "/v1/chat/twin-task", "/v1/chat/twin-handoff", "/v1/operations/read", "/v1/agents/list", "/v1/departments/list", "/v1/people/list"])
    TODAY_BUSINESS_PATHS.add(path);
for (const path of ['/v1/today/place', '/v1/today/undo', '/v1/today/recap', '/v1/today/arrive'])
    TODAY_BUSINESS_PATHS.add(path);
for (const path of ['/v1/directory/list', '/v1/directory/get', '/v1/demand/scorecard', '/v1/platform/gcp-costs'])
    TODAY_BUSINESS_PATHS.add(path);
for (const path of ['/v1/experience/line', '/v1/drive/folders', '/v1/drive/files', '/v1/documents/read', '/v1/merges/list', '/v1/conversations/list', '/v1/conversations/read', '/v1/conversations/draft'])
    TODAY_BUSINESS_PATHS.add(path);
//# sourceMappingURL=today-workspace.js.map